import { useCallback, useEffect, useRef, useState } from "react";
import { startCompanionLlmStream } from "../services/companionGraphqlSseClient";
import { AttachmentInput } from "@axio/graphql/generated/graphql";

export type LearningSignal = {
  detected: boolean;
  topic?: string;
  context?: string;
};

export type StreamCompletionEvent = {
  chatId?: string;
  messageId?: string;
  metadata?: {
    learningSignal?: LearningSignal;
    createdILE?: {
      id: string;
      title: string;
      description?: string | null;
      thumbnail?: string | null;
      objectives?: Array<{ id?: string; objective?: string | null; sequence?: number | null }>;
    };
  } | null;
};
export interface CompanionStreamToolsInput {
  deepResearch?: boolean;
  imageGenerationEnabled?: boolean;
}

interface StartStreamOptions {
  chatId?: string;
  workspaceId?: string;
  tools?: CompanionStreamToolsInput;
  attachment?: AttachmentInput;
  timeZone?: string;
  timestamp?: string;
  userMessageId?: string;
  isEditReplay?: boolean;
  anchorMessageId?: string;
  branchId?: string | null;
  onChunk?: (payload: { text: string; messageId?: string; isFinalChunk?: boolean }) => void;
  onComplete?: (payload: { event?: StreamCompletionEvent; text: string }) => void;
  onError?: (payload: { error: Error }) => void;
  onChatRenamed?: (payload: { chatId: string; name: string }) => void;
  onStatus?: (payload: { chatId: string; data: string }) => void;
  onToolStart?: (payload: { chatId: string; tool: string; jobId?: string | null }) => void;
  onToolResult?: (payload: { chatId: string; tool: string; result: Record<string, unknown> }) => void;
  onDeepSearchState?: (payload: { state: string; payload?: Record<string, unknown> | null }) => void;
}
export function useCompanionLlmStream() {
  const controllerRef = useRef<AbortController | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);

  useEffect(() => {
    return () => {
      controllerRef.current?.abort();
    };
  }, []);

  const startStream = useCallback(async (question: string, options: StartStreamOptions = {}) => {
    const {
      onComplete,
      onError,
      onChunk,
      onChatRenamed,
      onStatus,
      onToolStart,
      onToolResult,
      onDeepSearchState,
      timeZone,
      timestamp,
      ...streamOptions
    } = options;

    const resolvedTimeZone = timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
    const resolvedTimestamp = timestamp ?? new Date().toISOString();

    const mergedTools: CompanionStreamToolsInput = {
      ...(streamOptions.tools ?? {}),
    };
    const toolsVariable = Object.keys(mergedTools).length > 0 ? mergedTools : undefined;

    controllerRef.current?.abort();

    const controller = new AbortController();
    controllerRef.current = controller;

    setIsStreaming(true);

    let accumulatedText = "";
    let completionEvent: StreamCompletionEvent | undefined;

    try {
      await startCompanionLlmStream(
        question,
        controller,
        {
          onChunk: (event) => {
            if (event.__typename === "LLMStreamMessageDeltaEvent") {
              const delta = event.delta;
              if (delta?.__typename === "LLMStreamTextBlock" && delta.text) {
                accumulatedText += delta.text;
                onChunk?.({
                  text: delta.text,
                  messageId: event.messageId,
                });
              }
            }

            if (event.__typename === "LLMStreamMessageCompletedEvent") {
              completionEvent = {
                chatId: event.message?.chatId,
                messageId: event.message?.id,
                metadata: ((event.message?.metadata ?? null) as StreamCompletionEvent["metadata"]) ?? null,
              };
              setIsStreaming(false);
              onComplete?.({ event: completionEvent, text: accumulatedText });
            }

            if (event.__typename === "LLMStreamChatRenamedEvent") {
              onChatRenamed?.({
                chatId: event.chatId,
                name: event.name,
              });
            }

            if (event.__typename === "LLMStreamStatusEvent") {
              const e = event as unknown as { chatId: string; data: string };
              onStatus?.({ chatId: e.chatId, data: e.data });
            }

            const ev = event as unknown as {
              __typename?: string;
              chatId?: string;
              tool?: string;
              jobId?: string | null;
              result?: Record<string, unknown>;
            };
            if (ev.__typename === "LLMStreamToolStartEvent" && ev.chatId != null && ev.tool != null) {
              onToolStart?.({ chatId: ev.chatId, tool: ev.tool, jobId: ev.jobId });
            }
            if (ev.__typename === "LLMStreamToolResultEvent" && ev.chatId != null && ev.tool != null) {
              onToolResult?.({ chatId: ev.chatId, tool: ev.tool, result: ev.result ?? {} });
            }

            if (event.__typename === "LLMStreamDeepSearchStateEvent") {
              onDeepSearchState?.({
                state: event.state,
                payload: event.payload ?? undefined,
              });
            }

            if (event.__typename === "LLMStreamErrorEvent") {
              const message = event.errorMessage || "LLM stream error";
              setIsStreaming(false);
              onError?.({ error: new Error(message) });
            }
          },
          onError: (error) => {
            setIsStreaming(false);
            onError?.({ error });
          },
          onComplete: () => {
            setIsStreaming(false);
          },
        },
        {
          ...streamOptions,
          tools: toolsVariable,
          timeZone: resolvedTimeZone,
          timestamp: resolvedTimestamp,
        }
      );
    } catch (err) {
      if ((err as DOMException)?.name === "AbortError") return;
      setIsStreaming(false);
      onError?.({ error: err instanceof Error ? err : new Error(String(err)) });
      throw err;
    }
  }, []);

  const cancelStream = useCallback(() => {
    controllerRef.current?.abort();
    controllerRef.current = null;
    setIsStreaming(false);
  }, []);

  return { startStream, cancelStream, isStreaming };
}

export default useCompanionLlmStream;
